import { ProfilePageTabs, UIVideoResume, UserPercentileBucket } from 'types';
import ProfileIntroduction from './ProfileIntroduction';
import ProfileLevelupStats from './ProfileLevelupStats';
import ProfilePersonalitySection from './ProfilePersonalitySection';
import ProfileSkillSection from './ProfileSkillSection';
import ProfileTabs from './ProfileTabs';
import ProfileTechnicalScorecard from './ProfileTechnicalScorecard';
import ProfileWorkExperience from './ProfileWorkExperience';
import ProfileProjects from './ProfileProjects';
import ProfileEducation from './ProfileEducation';
import ProfileCertificate from './ProfileCertificate';
import { Button } from 'flowbite-react';
import CreateYourProfileBanner from './CreateYourProfileBanner';
import { Loader } from 'ui';
import React, { useEffect, useState } from 'react';
import { useGetVideoResumeByUserIdQuery } from 'src/hooks/videoResume.hooks';
import { useGetUserProfile } from 'src/hooks/profile.hooks';
import { useGetCompletedMeetingCount, useGetUserProfileScores } from 'src/hooks/user.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { isRecruiterSide } from 'src/config/env.config';
import { useLocation, useNavigate } from 'react-router-dom';
import TechnicalRoundRecordingsContainer from './technicalRoundRecordings/TechnicalRoundRecordingsContainer';
import SocialAccount from 'src/pages/profile/SocialAccount';
import { useGetAllMeetingsDetail } from 'src/hooks/meeting.hooks';
import { isAtLeastOneRecordingAvailable } from 'src/utils/interview';
import AdditionalInfo from './AdditionalInfo';
import { Collapse, useMediaQuery } from '@mui/material';
import { HiChevronRight } from 'react-icons/hi';
import {
	PiBackpackDuotone,
	PiChartPieSliceDuotone,
	PiFileText,
	PiLightningDuotone,
	PiSealCheckDuotone,
	PiShieldCheck,
	PiShieldCheckDuotone,
	PiShieldCheckeredDuotone,
	PiStarDuotone,
	PiUserRectangleDuotone,
} from 'react-icons/pi';
import Badges from './badges/Badges';
import classNames from 'classnames';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useInView } from 'react-intersection-observer';
import { IconType } from 'react-icons';
import StartHiringProfileBanner from './StartHiringProfileBanner';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';
import MultiProfileTabs from './multiProfileTabs/MultiProfileTabs';
import BehavioralFlagsDisplay from './BehavioralFlagsDisplay';

interface Props {
	userHandler: string;
	showBannerBg?: boolean;
	matchPercentage?: number;
	forceMobileView?: boolean;
	hasShownInterest?: boolean;
	profileBackground?: string;
}

export type PageContestType = {
	isImp?: boolean;
	icon: IconType;
	name: string;
	content: React.JSX.Element;
	hidden?: boolean;
};

const UserProfile = ({
	userHandler,
	matchPercentage,
	hasShownInterest,
	showBannerBg,
	forceMobileView,
	profileBackground,
}: Props) => {
	const [selectedProfileTab, setSelectedProfileTab] = useState<string>('');
	const location = useLocation();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [isMobileView, setIsMobileView] = useState(isMobile);
	const [isSelf, setIsSelf] = useState<boolean>(false); // is user himself/herself seeing the profile

	const { themeMode } = useThemeContext();
	const navigate = useNavigate();
	const { user, isJobseekerLoggedIn, isRecruiterLoggedIn, isAccountManagerLoggedIn, isUserUniversityManagerLoggedIn } =
		useUserContext();
	const queryParams = new URLSearchParams(location.search);

	const [expanded, setExpanded] = useState<number | false>(1);

	// Accessing individual query parameters
	const company = queryParams.get('company');
	const showScore = queryParams.get('showScore');
	const isPublicProfile = company === 'true' || isRecruiterLoggedIn || isUserUniversityManagerLoggedIn;
	const onlyPublicProfile = !isSelf && !isUserUniversityManagerLoggedIn && showScore !== 'true';
	const meetingCount = useGetCompletedMeetingCount(isJobseekerLoggedIn, userHandler ?? '');

	const [activeTab, setActiveTab] = useState<ProfilePageTabs>(ProfilePageTabs.Stats);

	const userProfile = useGetUserProfile(userHandler ?? '');
	const { data: userProfileScores } = useGetUserProfileScores(userHandler ?? '');

	const { data: allMeetingsDetailData, isLoading: isAllMeetingsDetailDataLoading } =
		useGetAllMeetingsDetail(userHandler);

	const videoResume = useGetVideoResumeByUserIdQuery(userProfile.data?.userDetail?._id ?? '');

	useEffect(() => {
		if (Object.values(ProfilePageTabs).includes(queryParams.get('selected') as ProfilePageTabs)) {
			setActiveTab(queryParams.get('selected') as ProfilePageTabs);
		} else {
			setActiveTab(ProfilePageTabs.Stats);
		}
		console.log('userProfile.data?.profileSpecificPercentiles', userProfile.data);
		if (userProfile.data?.userDetail && user?.handler) {
			const flag = userProfile.data.userDetail.handler === user?.handler ? true : false;
			setIsSelf(flag);
		}
	}, [userProfile.data, user?.handler]);

	useEffect(() => {
		if (!location.hash) return;
		const element = document.querySelector(location.hash);
		if (element) {
			element?.scrollIntoView();
		}
	}, [location.hash]);

	if (userProfile.isLoading || videoResume.isLoading || isAllMeetingsDetailDataLoading) {
		return (
			<div className="pt-8">
				{/* <Loader /> */}
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
			</div>
		);
	}

	if (!userProfile.data || !userProfile.data.userDetail) {
		return <div className="flex items-center justify-center py-10 text-[#FAFAFC]">Profile Does not Exist</div>;
	}

	const showRecordingsTab =
		(isSelf || isPublicProfile || isRecruiterSide || showScore === 'true' || true) &&
		isAtLeastOneRecordingAvailable(allMeetingsDetailData);
	const showSkillsDetails =
		userProfile.data.userDetail.evaluation.skillRatings?.length ||
		userProfile.data.userDetail.profile.profileSkills?.length ||
		meetingCount.data?.interviewedByDetails?.length;

	const getProfilePageContent = (selectedTab: string, selectedProfileTab: string) => {
		const selectedProfile = allMeetingsDetailData?.mergedUserPreference?.[selectedProfileTab];
		const skillRatings = selectedProfile?.skillRatings || [];
		console.log('skillRatings in getProfilePageContent', skillRatings);
		const meetingIds = selectedProfile?.meetings || [];
		const profileMeetings = allMeetingsDetailData?.meetings?.filter(m =>
			meetingIds.map(id => id?.toString()).includes(m?._id?.toString()),
		);
		switch (selectedTab) {
			case ProfilePageTabs.Stats:
				return [
					{
						icon: PiFileText,
						name: 'TECHNICAL SCORECARD',
						// EARLIER: userProfile.data.userDetail?.evaluation?.skillRatings
						hidden: !skillRatings?.filter(el => el.rating > 0 && el?.skill?.skillGroupType)?.length,
						content: (
							<div className="">
								{skillRatings?.filter(el => el.rating > 0 && el?.skill?.skillGroupType)?.length ? (
									<ProfileTechnicalScorecard
										title="TECHNICAL SCORECARD"
										showingToCompany={onlyPublicProfile}
										levelProgressionData={skillRatings?.filter(el => el.rating > 0 && el?.skill?.skillGroupType) ?? []}
									// isSelf={isSelf}

									// profileOwnerName={userProfile.data.userDetail?.name}
									/>
								) : null}
							</div>
						),
					},
					{
						icon: PiShieldCheckDuotone,
						name: 'SKILLS',
						hidden:
							skillRatings?.filter(skill => skill.rating > 0).length === 0 ||
							isRecruiterLoggedIn ||
							isAccountManagerLoggedIn ||
							company === 'true',
						content: (
							<>
								<Badges
									title="SKILLS"
									skills={skillRatings?.filter(el => el.rating > 0 && el?.skill?.skillGroupType) ?? []}
								// isSelf={isSelf}
								// userProfile={userProfile?.data}
								// interviewedByDetails={meetingCount.data?.interviewedByDetails ?? []}
								// resumeSkills={userProfile.data.userDetail?.profile?.profileSkills ?? []}
								/>
							</>
						),
					},
					{
						isImp: false,
						icon: PiUserRectangleDuotone,
						name: 'ASSESSMENT',
						hidden: !showRecordingsTab,
						content: (
							<>
								<TechnicalRoundRecordingsContainer title="ASSESSMENT" meetings={profileMeetings} />
							</>
						),
					},
					{
						icon: PiChartPieSliceDuotone,
						name: 'GITHUB STATS',
						hidden: !(
							(!isSelf && userProfile.data.userDetail?.flags?.isGitHubConnected) ||
							(isPublicProfile && userProfile?.data?.userDetail?.flags?.isGitHubConnected) ||
							(isSelf && !isPublicProfile)
						),
						content: (
							<>
								{((!isSelf && userProfile.data.userDetail?.flags?.isGitHubConnected) ||
									(isPublicProfile && userProfile?.data?.userDetail?.flags?.isGitHubConnected) ||
									(isSelf && userProfile?.data?.userDetail && !isPublicProfile)) && (
										<SocialAccount
											title="GITHUB STATS"
											data={userProfile.data.userDetail}
											id="githubStats"
											isSelf={isSelf}
										/>
									)}
							</>
						),
					},
				];
			case ProfilePageTabs.Profile:
				return [
					{
						icon: PiUserRectangleDuotone,
						name: 'STUDENT INFO',
						hidden: !((isSelf || isPublicProfile || isRecruiterSide) && userProfile?.data?.userDetail),
						content: (
							<>
								{(isSelf || isPublicProfile || isRecruiterSide) && userProfile?.data?.userDetail ? (
									<AdditionalInfo
										isSelf={isSelf}
										title="STUDENT INFO"
										isAccountManagerLoggedIn={isAccountManagerLoggedIn}
										data={userProfile?.data?.userDetail}
									/>
								) : null}
							</>
						),
					},
					{
						icon: PiFileText,
						name: 'WORK EXPERIENCE',
						hidden: !(
							isSelf ||
							(userProfile.data.workExperience?.length &&
								userProfile.data.workExperience?.filter(el => el?.jobTitle)?.length)
						),
						content: (
							<>
								<ProfileWorkExperience
									isSelf={isSelf}
									title="WORK EXPERIENCE"
									isPublicProfile={isPublicProfile}
									workExperience={userProfile.data.workExperience ?? []}
								/>
							</>
						),
					},
					{
						icon: PiShieldCheckeredDuotone,
						name: 'PROJECTS',
						hidden: !(isSelf || userProfile.data.projectDetails?.length),
						content: (
							<>
								<ProfileProjects
									title="PROJECTS"
									userProjects={userProfile.data.projectDetails ?? []}
									isSelf={isSelf}
									isPublicProfile={isPublicProfile}
								/>
							</>
						),
					},
					{
						icon: PiBackpackDuotone,
						name: 'EDUCATION',
						hidden: !(isSelf || userProfile.data.educationDetails?.length),
						content: (
							<>
								<ProfileEducation
									title="EDUCATION"
									isSelf={isSelf}
									isPublicProfile={isPublicProfile}
									userEducation={userProfile.data.educationDetails ?? []}
								/>
							</>
						),
					},
					{
						icon: PiSealCheckDuotone,
						name: 'CERTIFICATES',
						hidden: !(isSelf || userProfile.data.certfifcateDetails?.length),
						content: (
							<>
								<ProfileCertificate
									title="CERTIFICATES"
									isSelf={isSelf}
									isPublicProfile={isPublicProfile}
									userCertificate={userProfile.data.certfifcateDetails ?? []}
								/>
							</>
						),
					},
					{
						icon: PiStarDuotone,
						name: 'PERSONALITY',
						hidden: !(isSelf || isPublicProfile || isRecruiterSide) || !(isSelf || videoResume.data?.videoResume),
						content: (
							<>
								{isSelf || isPublicProfile || isRecruiterSide ? (
									<ProfilePersonalitySection
										title="PERSONALITY"
										userVideos={videoResume.data?.videoResume as UIVideoResume}
										id={'personality'}
										isSelf={isSelf}
									/>
								) : null}
							</>
						),
					},
				];
			default:
				return [{ icon: PiShieldCheck, name: '', content: <>No content found</>, hidden: false }];
		}
	};

	return (
		<div className="">
			{/************************* VISIBLE ON PUBLIC PROFILE PAGE ONLY *************************/}
			{!isSelf ? (
				<>
					{/* when user is not signed in and is on someone else's profile page */}
					{!user && !isPublicProfile && <CreateYourProfileBanner />}
					{/* {!user && company && <StartHiringProfileBanner />} */}
				</>
			) : null}

			<div
				className={classNames(
					'w-full flex flex-col gap-5 isolate bg-[#EFF2F5] dark:bg-[inherit]',
					company ? 'pb-8 md:pb-60' : '',
					profileBackground,
				)}
			>
				{/************************* USER DETAILS BANNER *************************/}
				{/* Profile Intoduction [Profile Pic, Name, Exp, Profile Decsription, Social links]*/}
				<ProfileIntroduction
					isSelf={isSelf}
					showBannerBg={showBannerBg}
					forceMobileView={forceMobileView}
					matchPercentage={matchPercentage}
					hasShownInterest={hasShownInterest}
					showingToPublic={onlyPublicProfile}
					userProfileInfo={userProfile.data.userDetail}
					userPercentileBucketData={userProfile.data?.profileSpecificPercentiles?.[selectedProfileTab]}
				/>

				{/* behavioural flags */}
				{isSelf && userProfile.data?.userDetail?._id && (
					<div className="max-w-[1024px] w-full mx-auto px-4 lg:px-0">
						<BehavioralFlagsDisplay userId={userProfile.data.userDetail._id} isSelf={isSelf} />
					</div>
				)}

				<div className="max-w-[1024px] w-full mx-auto px-4 lg:px-0 ">
					<div className="">
						<ProfileTabs
							activeTab={activeTab}
							setActiveTab={(data: ProfilePageTabs) => {
								queryParams.set('selected', data);
								navigate(`?${queryParams.toString()}`);
								setActiveTab(data);
								setExpanded(1);
							}}
							isSelf={!!isSelf}
							showBannerBg={!!showBannerBg}
							userProfileInfo={userProfile.data.userDetail}
							forceMobileView={forceMobileView}
						/>

						<div className={classNames('flex items-stretch gap-4 ')}>
							<div className="w-full py-3 flex-1 ">
								{getProfilePageContent(activeTab, selectedProfileTab)?.length > 0 && expanded ? (
									<div className="overflow-auto h-full">
										{activeTab === ProfilePageTabs.Stats ? (
											<div>
												<MultiProfileTabs
													selectedProfileTab={selectedProfileTab}
													setSelectedProfileTab={setSelectedProfileTab}
													allMeetingsDetailData={allMeetingsDetailData}
													profileSpecificPercentiles={userProfile.data?.profileSpecificPercentiles}
													onlyPublicProfile={onlyPublicProfile}
												/>
											</div>
										) : null}
										{getProfilePageContent(activeTab, selectedProfileTab)
											?.filter(el => !el?.hidden)
											.map((el, index) => (
												<TabsContentRenderer
													data={el}
													key={el.name}
													onInview={el => {
														setExpanded(index + 1);
													}}
												/>
											))}
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UserProfile;

const TabsContentRenderer = ({
	data,
	onInview,
}: {
	data: PageContestType;
	onInview: (el: PageContestType) => void;
}) => {
	const { ref } = useInView({
		/* Optional options */
		threshold: 0.5,
		onChange: (view, entry) => {
			if (view) {
				onInview(data);
			}
		},
	});

	return (
		<div
			ref={ref}
			className="scroll-m-[80px] pb-8 last-of-type:pb-0"
			id={data.name?.split(' ').join('-').toLowerCase()}
		>
			<div className="bg-white dark:bg-[#1F2A37] rounded-[8px]">{data.content}</div>
		</div>
	);
};
